import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { promptTemplateService } from '../services/api';
import type { PromptTemplate, CreateTemplateRequest, PromptTemplateCategory } from '@personawiz/shared';

const categories: PromptTemplateCategory[] = [
  'persona-generation',
  'content-creation',
  'code-generation',
  'analysis',
  'summarization',
  'translation',
  'custom',
];

interface FormData {
  name: string;
  description: string;
  category: PromptTemplateCategory;
  systemPrompt: string;
  userPromptTemplate: string;
  tags: string[];
}

export default function PromptTemplates() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    category: 'custom',
    systemPrompt: '',
    userPromptTemplate: '',
    tags: [],
  });
  const [isSaving, setIsSaving] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<PromptTemplate | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await promptTemplateService.list({ limit: 50 });
      setTemplates(response.data);
    } catch {
      setError('Failed to load templates.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadTemplates();
    }, 0);

    return () => clearTimeout(timer);
  }, [loadTemplates]);

  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: 'custom',
      systemPrompt: '',
      userPromptTemplate: '',
      tags: [],
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (template: PromptTemplate) => {
    setDialogMode('edit');
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category,
      systemPrompt: template.systemPrompt,
      userPromptTemplate: template.userPromptTemplate,
      tags: template.tags,
    });
    setDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const parseVariablesFromTemplate = (template: string): { name: string; required: boolean }[] => {
    const matches = template.match(/\{\{(\w+)\}\}/g) || [];
    const varNames = [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
    return varNames.map(name => ({ name, required: true }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const variables = parseVariablesFromTemplate(formData.userPromptTemplate);
      const templateVariables = variables.map(v => ({
        name: v.name,
        description: `Variable: ${v.name}`,
        required: v.required,
        type: 'string' as const,
      }));

      const requestData: CreateTemplateRequest = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        systemPrompt: formData.systemPrompt,
        userPromptTemplate: formData.userPromptTemplate,
        variables: templateVariables,
        tags: formData.tags,
        isPublic: false,
      };

      if (dialogMode === 'create') {
        const created = await promptTemplateService.create(requestData);
        setTemplates([created, ...templates]);
      } else if (editingTemplate) {
        const updated = await promptTemplateService.update(editingTemplate.id, {
          ...requestData,
          templateId: editingTemplate.id,
        });
        setTemplates(templates.map(t => t.id === updated.id ? updated : t));
      }
      handleCloseDialog();
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save template.';
      setError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!menuTemplate || !window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await promptTemplateService.delete(menuTemplate.id);
      setTemplates(templates.filter(t => t.id !== menuTemplate.id));
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete template.';
      setError(errorMsg);
    }
    setMenuAnchor(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: PromptTemplate) => {
    setMenuAnchor(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTemplate(null);
  };

  const handleCopyContent = (template: PromptTemplate) => {
    const content = `System Prompt:\n${template.systemPrompt}\n\nUser Template:\n${template.userPromptTemplate}`;
    navigator.clipboard.writeText(content);
    setMenuAnchor(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Prompt Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and version your reusable prompt templates.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          New Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first prompt template to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Variables</strong></TableCell>
                <TableCell><strong>Version</strong></TableCell>
                <TableCell><strong>Updated</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={template.category.replace(/-/g, ' ')} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {template.variables.slice(0, 3).map((v) => (
                        <Chip key={v.name} label={v.name} size="small" variant="outlined" />
                      ))}
                      {template.variables.length > 3 && (
                        <Chip label={`+${template.variables.length - 3}`} size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={`v${template.version}`} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)}>
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => menuTemplate && handleOpenEditDialog(menuTemplate)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={() => menuTemplate && handleCopyContent(menuTemplate)}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          Copy Content
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Template' : 'Edit Template'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value as PromptTemplateCategory })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat.replace(/-/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="System Prompt"
              value={formData.systemPrompt}
              onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
              multiline
              rows={4}
              placeholder="Enter the system prompt that sets the AI's behavior..."
              required
            />
            <TextField
              fullWidth
              label="User Prompt Template"
              value={formData.userPromptTemplate}
              onChange={(e) => setFormData({ ...formData, userPromptTemplate: e.target.value })}
              multiline
              rows={6}
              placeholder="Enter the user prompt template. Use {{variableName}} for variables..."
              helperText="Use {{variableName}} syntax to define variables that will be filled in when rendering."
              required
            />
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
              })}
              helperText="e.g., marketing, email, onboarding"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving || !formData.name || !formData.systemPrompt || !formData.userPromptTemplate}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
